import {FC, useState} from "react";
import {Box, Link, Paper, Stack, Typography} from "@mui/material";
import {useAppDispatch, useAppSelector} from "../../../hooks/redux";
import {selectCurrentUser} from "../../users/model/selectors";
import {fetchCheckAuth} from "../../auth/model/actions";
import {setMessage} from "../../messages/model/slice";
import {MESSAGE_SEVERITY} from "../../../utils/const";
import {routes} from "../../../utils/routes";
import MyButton from "../../../styles/theme/customizations/MyButton";
import {legalAPI} from "../api";
import {useIsReadOnly} from "../../../hooks/useIsReadOnly";

// Выбор гостя — только в браузере: демо-аккаунт один на всех посетителей, и
// серверная запись применила бы выбор первого посетителя ко всем следующим
// (а POST гостя бэкенд и так режет). Версия в ключе — новая политика спросит снова.
const guestChoiceKey = (version: number) => `cookie-choice:v${version}`;

const readGuestChoice = (version: number): boolean => {
    try {
        return localStorage.getItem(guestChoiceKey(version)) !== null;
    } catch {
        // Приватный режим/заблокированное хранилище — выбора нет.
        return false;
    }
};

const writeGuestChoice = (version: number, analytics: boolean): void => {
    try {
        localStorage.setItem(guestChoiceKey(version), analytics ? "all" : "necessary");
    } catch {
        /* см. readGuestChoice — баннер закроется до конца сессии вкладки */
    }
};

/**
 * Нижний баннер cookie. Мягкий режим: два равноправных выбора, оба сохраняются
 * на беке (у гостя — в браузере), баннер закрывается. Без блокировки работы и без разлогина.
 * Показ — только в залогиненной зоне, при needs_cookie_choice и при отсутствии
 * незакрытого блокирующего согласия (ConsentReacceptModal приоритетнее).
 */
const CookieConsentBanner: FC = () => {
    const dispatch = useAppDispatch();
    const currentUser = useAppSelector(selectCurrentUser);
    const isReadOnly = useIsReadOnly();
    const cookieVersion = currentUser?.legal_versions?.cookie_policy ?? null;
    const [submitting, setSubmitting] = useState(false);
    const [guestChoseNow, setGuestChoseNow] = useState(false);

    const guestChoiceMade =
        isReadOnly && (guestChoseNow || (cookieVersion !== null && readGuestChoice(cookieVersion)));

    const open =
        !!currentUser?.needs_cookie_choice &&
        !currentUser?.needs_consent &&
        !currentUser?.consent_withdrawn &&
        !guestChoiceMade;

    if (!open) return null;

    const handleChoice = async (analytics: boolean) => {
        if (!cookieVersion) return;
        if (isReadOnly) {
            writeGuestChoice(cookieVersion, analytics);
            setGuestChoseNow(true);
            return;
        }
        setSubmitting(true);
        try {
            await legalAPI.cookieAccept({
                cookie_version: cookieVersion,
                analytics,
            });
            // Перечитываем /auth/me — needs_cookie_choice станет false, баннер скроется.
            await dispatch(fetchCheckAuth());
        } catch {
            dispatch(setMessage({
                severity: MESSAGE_SEVERITY.error,
                text: "Не удалось сохранить выбор. Попробуйте ещё раз.",
            }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Paper
            elevation={8}
            sx={{
                position: "fixed",
                left: {xs: 8, md: 16},
                right: {xs: 8, md: 16},
                bottom: {xs: 8, md: 16},
                zIndex: (theme) => theme.zIndex.snackbar,
                maxWidth: 720,
                mx: "auto",
                p: {xs: 2, md: 2.5},
                border: 1,
                borderColor: "divider",
                borderRadius: 3,
            }}
        >
            <Stack spacing={1.5}>
                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        Мы используем файлы cookie
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Небольшие текстовые файлы нужны для входа и безопасности сессии.
                        Аналитические cookie помогают нам становиться лучше — включите их
                        по желанию. Подробнее — в{" "}
                        <Link href={routes.legalCookies} target="_blank" rel="noopener noreferrer">
                            Политике использования cookie
                        </Link>
                        .
                    </Typography>
                </Box>
                <Stack
                    direction={{xs: "column", sm: "row"}}
                    spacing={1}
                    justifyContent="flex-end"
                >
                    <MyButton
                        variant="outlined"
                        color="neutral"
                        onClick={() => handleChoice(false)}
                        disabled={submitting || !cookieVersion}
                    >
                        Только необходимые
                    </MyButton>
                    <MyButton
                        variant="contained"
                        onClick={() => handleChoice(true)}
                        loading={submitting}
                        disabled={submitting || !cookieVersion}
                    >
                        Принять всё
                    </MyButton>
                </Stack>
            </Stack>
        </Paper>
    );
};

export default CookieConsentBanner;
